import classNames from "classnames";
import uniqueId from "lodash/uniqueId";
import { Icon } from "PFComponents/icon";
import { uploadAttachment } from "PFCore/services/attachments/upload_attachment";
import { Attachment } from "PFTypes";
import { ChangeEvent, useRef, useState } from "react";

import css from "./chat_attachment_input.module.scss";
const ATTACHMENTS_LIMIT = 5; // api enforced

export type ChatAttachmentItem = Omit<Partial<Attachment>, "id"> & {
  id: string | number;
  fake?: boolean;
  uploadPromise?: Promise<unknown>;
  fileObjectUrl?: string;
};

type ChatAttachmentInputProps = {
  attachments: ChatAttachmentItem[];
  removeAttachment: (attachment: ChatAttachmentItem) => void;
  handleAttachmentSaved: (attachment: ChatAttachmentItem) => void;
  handleAttachmentSaveFailed: (error: unknown) => void;
};

export const ChatAttachmentInput = ({
  attachments,
  removeAttachment,
  handleAttachmentSaved,
  handleAttachmentSaveFailed
}: ChatAttachmentInputProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setLoading(true);

    const file = (e.target.files as FileList)[0];

    const uploadPromise = uploadAttachment(file);

    const fakeAttachment: ChatAttachmentItem = {
      id: uniqueId("fake"),
      fake: true,
      uploadPromise,
      fileObjectUrl: URL.createObjectURL(file)
    };

    handleAttachmentSaved(fakeAttachment);

    uploadPromise
      .then((resp: ChatAttachmentItem) => {
        resp.fileObjectUrl = fakeAttachment.fileObjectUrl;
        handleAttachmentSaved(resp);
      })
      .catch((resp) => {
        handleAttachmentSaveFailed(resp);
      })
      .finally(() => {
        removeAttachment(fakeAttachment);
        setLoading(false);
      });

    formRef.current?.reset(); // so you can add the same file again
  };

  const disabled = loading || attachments.length >= ATTACHMENTS_LIMIT;

  return (
    <label className={classNames(css.label, { [css.disabled]: disabled })}>
      <Icon name="paper-clip" size="sm" />
      <form ref={formRef}>
        <input disabled={disabled} className={css.input} type="file" onChange={handleChange} />
      </form>
    </label>
  );
};
